import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  useGetDairyManureAdditives,
  useGetFootprintingDairyManureAdditive,
  useGetFootprintingDairyPrimaryDataById,
  useMutateFootprintingDairyManureAdditive,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import {
  DAIRY_PRACTICE_SLUG,
  ENUM_DAIRY_MANURE_ADDITIVE_UNITS,
  ENUM_FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE,
  FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE,
  UploadableManureAdditive,
} from "models";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  KlimRadioGroup,
  Option,
  PageSpin,
  Select,
} from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const feedAdditivePlaceholder: UploadableManureAdditive = {
  id: undefined,
  manureAdditiveId: "",
  amountUsedM3: null,
  amountUsedM3Unit: null,
  enrichedSlurryPct: null,
};

export const ManureAdditiveForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);

  const [manureAdditiveActive, setManureAdditiveActive] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE | null>(null);
  const [appliedManureAdditives, setAppliedManureAdditives] = useState<UploadableManureAdditive[]>([]);

  const { data: dairyPrimaryDataQuery, isLoading } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { data: manureAdditivesQuery } = useGetFootprintingDairyManureAdditive(primaryDataId);
  const { data: dairyManureAdditivesQuery } = useGetDairyManureAdditives();

  const selectGroupName = "footprinting-dairy-manure-additive-select";

  const { mutate: mutateDairyManureAdditives } = useMutateFootprintingDairyManureAdditive(
    farmId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedManureAdditive: appliedManureAdditives.filter((m) => !!m.manureAdditiveId),
    },
    primaryDataId
  );

  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      manureAdditiveActive,
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyPrimaryData();
        mutateDairyManureAdditives();
      }
    },
    [isLoading, mutateDairyPrimaryData, mutateDairyManureAdditives, readOnly]
  );

  // Load feed additives data
  useEffect(() => {
    if (manureAdditivesQuery) {
      setAppliedManureAdditives(
        manureAdditivesQuery.length
          ? manureAdditivesQuery.map((m) => ({
              id: m.id,
              manureAdditiveId: m.manureAdditiveId ?? "",
              amountUsedM3: m.amountUsedM3 ?? null,
              amountUsedM3Unit: m.amountUsedM3Unit ?? null,
              enrichedSlurryPct: m.enrichedSlurryPct ?? null,
            }))
          : [{ ...feedAdditivePlaceholder }]
      );
    }
  }, [manureAdditivesQuery]);

  // Load primary data
  useEffect(() => {
    if (dairyPrimaryDataQuery) {
      setManureAdditiveActive(dairyPrimaryDataQuery.manureAdditiveActive ?? null);

      formRef.current?.setFieldsValue({
        manureAdditiveActive: dairyPrimaryDataQuery.manureAdditiveActive ?? null,
      });

      if (dairyPrimaryDataQuery.manureAdditiveActive !== null && dairyPrimaryDataQuery.manureAdditiveActive !== undefined) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyPrimaryDataQuery]);

  // This ensures that all form fields rerender when the non-last additive
  // is removed (i.e. they appear to slide up)
  useEffect(() => {
    if (appliedManureAdditives.length > 0) {
      formRef.current?.resetFields();
    }
  }, [appliedManureAdditives.length]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-400 padding-right-400 margin-left-100 margin-right-100">{t("dairyManureAdditiveForm.title")}</h1>
      <Card dataTestId="footprinting-dairy-manure-additives-form" className="margin-bottom-100 padding-100">
        <Flex align="center" gap={16} justify="space-between" className="padding-100">
          <span style={{ maxWidth: "64%" }}>
            {t("dairyManureAdditiveForm.manureAdditivesQuestion")}
            <span className="required-asterisk">*</span>
          </span>
          <KlimRadioGroup
            required
            name="manureAdditiveActive"
            dataTestId="footprinting-dairy-manure-additives-radio"
            disabled={readOnly}
            vertical={false}
            type="tertiary"
            size="large"
            onChange={({ target }) => {
              setManureAdditiveActive(target.value);
              if (target.value === FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.YES && appliedManureAdditives.length === 0) {
                setAppliedManureAdditives([{ ...feedAdditivePlaceholder }]);
              }
            }}
            value={manureAdditiveActive}
            defaultValue={manureAdditiveActive}
            optionType="button"
            options={[FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.YES, FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.NO].map((manureAdditiveActive) => ({
              label: t(`dairyManureAdditiveForm.manureAdditiveOptions.${manureAdditiveActive}`),
              value: manureAdditiveActive,
            }))}
          />
        </Flex>
        <Form ref={formRef}>
          {manureAdditiveActive === FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.YES &&
            appliedManureAdditives.map((additive, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;
              const updateItem = (item: UploadableManureAdditive) => {
                const next = [...appliedManureAdditives];
                next[index] = { ...item };
                setAppliedManureAdditives(next);
              };

              return (
                <Form.Item key={index} className="padding-left-100 padding-right-100 padding-bottom-0 padding-top-75">
                  <Flex className="margin-bottom-100 margin-top-100" justify="space-between" align="center" vertical={false}>
                    <h5 style={{ flex: 1 }}>{t("dairyManureAdditiveForm.additiveNumber", { count: index + 1 })}</h5>
                    <IconButton
                      type="tertiary"
                      disabled={readOnly}
                      dataTestId={`delete-manure-additive-${index}`}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => setAppliedManureAdditives([...appliedManureAdditives].filter((_, i) => i !== index))}
                    />
                  </Flex>
                  {/* @ts-expect-error need to convert to tsx */}
                  <Select
                    disabled={readOnly}
                    className="margin-bottom-150"
                    name={selectGroupWithIndex}
                    dataTestId={selectGroupWithIndex}
                    label={t("dairyManureAdditiveForm.question.manureAdditiveTypeQuestionInput")}
                    rules={[{ required: true, message: false }]}
                    initialValue={dairyManureAdditivesQuery?.find((f) => f.id === additive.manureAdditiveId)?.name || ""}
                    value={dairyManureAdditivesQuery?.find((f) => f.id === additive.manureAdditiveId)?.name || ""}
                    onChange={(v) => {
                      const matchingPractice = dairyManureAdditivesQuery?.find((f) => f.name === v);
                      updateItem({
                        ...additive,
                        manureAdditiveId: matchingPractice ? matchingPractice.id : "",
                      });
                    }}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                  >
                    {(dairyManureAdditivesQuery ?? []).map((dairyManureAdditive) => {
                      // Check if this additive ID is already used in another item
                      const isAlreadyUsed = appliedManureAdditives.some(
                        (a) => a.manureAdditiveId === dairyManureAdditive.id && a.manureAdditiveId !== additive.manureAdditiveId
                      );

                      return (
                        <Option
                          disabled={readOnly || isAlreadyUsed}
                          key={`${selectGroupWithIndex}-${dairyManureAdditive.id}`}
                          data-testid={`${selectGroupWithIndex}-${dairyManureAdditive.id}`}
                          value={dairyManureAdditive.name}
                        >
                          {t(`${DAIRY_PRACTICE_SLUG.MANURE_ADDITIVES}.${dairyManureAdditive.translationSlug}`, { ns: "dairy" }) || dairyManureAdditive.name}
                        </Option>
                      );
                    })}
                  </Select>

                  <InputNumberWithUnitSelector
                    key={`amount-${index}-${additive.manureAdditiveId}`}
                    className="floating-label margin-bottom-150"
                    disabled={readOnly}
                    dataTestId={`footprinting-dairy-manure-additive-amount-input-${index}`}
                    name={`amountUsedM3-${index}`}
                    label={t("dairyManureAdditiveForm.question.amountUsedQuestionInput")}
                    unitLabel={t("unit", { ns: "footprinting" })}
                    units={["kilograms", "liters"]}
                    unitDisplayOverride={{
                      kilograms: "kg / m³",
                      liters: "l / m³",
                    }}
                    value={additive.amountUsedM3}
                    initialValue={additive.amountUsedM3}
                    unitValue={additive.amountUsedM3Unit}
                    onChange={(v, unit) => updateItem({ ...additive, amountUsedM3: v, amountUsedM3Unit: unit as ENUM_DAIRY_MANURE_ADDITIVE_UNITS })}
                    validateFirst
                    rules={[
                      {
                        required: true,
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                            return Promise.reject(new Error(t("dairyManureAdditiveForm.error.amountUsedError")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />

                  <Form.Item>
                    <InputNumber
                      disabled={readOnly}
                      name={`enrichedSlurryPct-${index}`}
                      dataTestId="footprinting-manure-enriched-slurry-input"
                      label={t("dairyManureAdditiveForm.question.enrichedSlurryQuestionInput")}
                      suffix="%"
                      value={formatFractionInput(additive.enrichedSlurryPct)}
                      initialValue={formatFractionInput(additive.enrichedSlurryPct)}
                      onChange={(v) => updateItem({ ...additive, enrichedSlurryPct: formatPercentageInput(v) })}
                      validateFirst
                      rules={[
                        { required: true },
                        { type: "number" },
                        {
                          validator: (_: unknown, v: number) => {
                            if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                              return Promise.reject(new Error(t("dairyManureAdditiveForm.error.validPercentage")));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    />
                  </Form.Item>
                </Form.Item>
              );
            })}
          {manureAdditiveActive === FARM_FOOTPRINTING_DAIRY_MANURE_ADDITIVE_ACTIVE.YES && (
            <Button
              className="margin-top-150"
              type="text"
              dataTestId="add-manure-additive-button"
              disabled={readOnly}
              onClick={() => {
                setAppliedManureAdditives([...appliedManureAdditives, { ...feedAdditivePlaceholder }]);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("dairyManureAdditiveForm.add")}
            </Button>
          )}
        </Form>
      </Card>
    </div>
  );
};
